import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FiMail, FiBell, FiFileText, FiInfo, FiUser } from "react-icons/fi";
import { updateProfile } from "../../redux/slices/profileSlice";
import { refreshUserProfile } from "../../redux/slices/authSlice";
import { PLAN_SLUGS } from "../../constants/plans";
import "./EmailCaptureModal.scss";

const SESSION_KEY = "emailCaptureModalDismissed";

// Shown to Pro / Pro+Newsletter users whose email is NULL in the DB.
// Dismissible via Skip — dismissed state is persisted in sessionStorage.
const EmailCaptureModal = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "true"
  );

  const slug = user?.plan?.slug;
  const needsEmail =
    !user?.email &&
    slug === PLAN_SLUGS.PRO_NEWSLETTER &&
    !dismissed;

  if (!needsEmail) return null;

  const handleSkip = () => {
    sessionStorage.setItem(SESSION_KEY, "true");
    setDismissed(true);
  };

  const validate = (val) => {
    if (!val.trim()) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()))
      return "Please enter a valid email address.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setLoading(true);
    try {
      const result = await dispatch(
        updateProfile({ email: email.trim(), name: user.full_name || "" })
      ).unwrap();

      if (result?.actionFlag === "PROFILE_UPDT_ERR") {
        setError("That email is already in use. Please try a different one.");
        setLoading(false);
        return;
      }
      await dispatch(refreshUserProfile());
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="ecm-modal"
    >
      <Modal.Body>
        {/* <div className="ecm-handle" aria-hidden="true" /> */}

        {/* Compound icon */}
        <div className="ecm-icon-wrap">
          <FiMail className="ecm-icon-mail" />
          <div className="ecm-icon-user-badge">
            <FiUser size={11} />
          </div>
        </div>

        <h2 className="ecm-heading">Add your email to activate your newsletter</h2>
        <p className="ecm-desc">
          Your Pro + Newsletter subscription includes email newsletters and
          important updates. Add your email address to start receiving your
          benefits.
        </p>

        <form onSubmit={handleSubmit}>
          <label className="ecm-label">Email address</label>
          <div className="ecm-input-wrap">
            <input
              type="email"
              className={error ? "is-invalid" : ""}
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              autoFocus
              disabled={loading}
            />
            <FiMail className="ecm-input-icon" />
          </div>
          {error && <div className="ecm-error">{error}</div>}

          {/* Why box */}
          <div className="ecm-why-box">
            <div className="ecm-why-title">
              <FiInfo size={16} />
              Why do we need your email?
            </div>
            <div className="ecm-why-item">
              <FiMail />
              Receive premium newsletters
            </div>
            <div className="ecm-why-item">
              <FiBell />
              Get important account updates
            </div>
            <div className="ecm-why-item">
              <FiFileText />
              Access all Pro + Newsletter benefits
            </div>
          </div>

          <button type="submit" className="ecm-btn" disabled={loading}>
            <FiMail size={17} />
            {loading ? "Saving…" : "Save Email & Continue"}
          </button>

          <button
            type="button"
            className="ecm-skip-btn"
            onClick={handleSkip}
            disabled={loading}
          >
            Skip for now
          </button>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default EmailCaptureModal;
